import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useGetMeQuery } from '../../store/api/authApi';
import { useLanguage } from '../../hooks/useLanguage';

// Admin Dashboard Icon
const AdminDashboardIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
    />
  </svg>
);

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  
  // Check if user is admin
  const { data: userData } = useGetMeQuery();
  const isAdmin = userData?.data?.user?.isAdmin || false;

  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Only show sidebar for admins
  if (!isAdmin) {
    return null;
  }

  const isActive = location.pathname.startsWith('/admin');

  return (
    <>
      {/* Overlay for mobile/tablet only - NOT for desktop */}
      {isOpen && !isDesktop && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fade-in"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-16 lg:top-20 h-[calc(100vh-4rem)] lg:h-[calc(100vh-5rem)] w-64 z-40 transform transition-all duration-300 ease-out ${
          isRTL ? 'right-0 border-l' : 'left-0 border-r'
        } ${
          isOpen ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full'
        } ${
          isScrolled
            ? 'bg-brand-primary/30 backdrop-blur-lg backdrop-saturate-150 border-brand-primary/40'
            : 'bg-transparent backdrop-blur-0 backdrop-saturate-100 border-transparent'
        }`}
        role="navigation"
        aria-label="Main navigation"
      >
        <nav className="p-4 h-full overflow-y-auto">
          <ul className="space-y-2">
            <li>
              <Link
                to="/admin/menu"
                onClick={() => {
                  // Only close on mobile/tablet, keep open on desktop
                  if (!isDesktop) {
                    onClose();
                  }
                }}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-body transition-all duration-200 ${
                  isActive
                    ? `bg-brand-accent/20 text-brand-accent ${isRTL ? 'border-l-2' : 'border-r-2'} border-brand-accent`
                    : 'text-neutral-light hover:bg-brand-primary/20 hover:text-brand-accent'
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                <AdminDashboardIcon />
                <span>{t('navigation.adminDashboard')}</span>
              </Link>
            </li>
          </ul>
        </nav>
      </aside>
    </>
  );
}
